import { gql } from '@apollo/client';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import { DeleteConfigurationModal } from '~components/configurations/DeleteConfigurationModal';
import { NAVBAR_HEIGHT } from '~components/customers/Navbar';
import {
  HorizontalTab,
  HorizontalTabList,
  HorizontalTabPanel,
  TabsUnderline,
} from '~components/ui/ConfigHorizontalTabs';
import { EditIcon } from '~components/ui/icons';
import { PAGE_CONTENT_PADDING_TOP } from '~components/ui/PageContent';
import { useTabs } from '~components/ui/Tabs';
import { ConfigurationStatus } from '~graphql/__generated__/types';
import { useContentBoxHeight } from '~utils/dimensions';
import { fromError } from '~utils/errors';
import { MaybePromise } from '~utils/types';
import { ConfigurationStateToggle, ConfigurationStatusType } from '../ConfigStateToggle';
import { useCreateDraftMutation } from '../ConfigurationsTable/Cell/__generated__/EditDeleteCell.graphql';
import { EditConfigurationModal } from '../EditConfigurationModal';
import {
  ConfigDetailSettings_ConfigFragment,
  ConfigDetailSettings_CustomerFragment,
} from './__generated__/ConfigDetailSettings.graphql';
import {
  ConfigDetailView_ConfigFragment,
  ConfigDetailView_CustomerFragment,
  useDeleteConfigurationMutation,
  usePublishConfigurationMutation,
  useSyncAllDisplaysToConfigurationMutation,
} from './__generated__/ConfigDetailView.graphql';
import ConfigDetailHeader from './ConfigDetailHeader';

interface Props {
  customer: ConfigDetailView_CustomerFragment;
  isDefaultConfiguration?: boolean;
  configuration: ConfigDetailView_ConfigFragment;
  title: string;
  refetchData: () => MaybePromise<void>;
  isConfigurationStatusToggleEnabled: boolean;
  onConfigurationStateChange: (status: ConfigurationStatusType) => void;
}

export function useConfigDetailSettingsTab() {
  return useOutletContext<{
    customer: ConfigDetailSettings_CustomerFragment;
    configuration: ConfigDetailSettings_ConfigFragment;
    refetchData: () => MaybePromise<void>;
    scrollOffset?: number;
    configStatus?: ConfigurationStatusType;
    onConfigurationStateChange: (status: ConfigurationStatusType) => void;
  }>();
}

export function ConfigDetailView({
  customer,
  isDefaultConfiguration,
  configuration,
  title,
  refetchData,
  isConfigurationStatusToggleEnabled,
  onConfigurationStateChange,
}: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { verifyUserPermissions } = useAuth();
  const canUpdate = verifyUserPermissions([Permission.ConfigurationUpdate]);
  const canDelete = verifyUserPermissions([Permission.ConfigurationDelete]);
  const headerRef = useRef<HTMLDivElement>(null);
  //const [targetRect, setTargetRect] = useState(headerRef?.current?.getBoundingClientRect());

  const location = useLocation();
  const showViewInPublishedState: boolean | null = location.state?.showPublished;

  const { tabs } = useTabs(
    [
      {
        path: 'config-summary',
        label: t('configSummary'),
      },
      {
        path: 'config-devices',
        label: t('configDevices'),
      },
      {
        path: 'settings',
        label: t('settings'),
      },
      {
        path: 'firmware',
        label: t('firmware'),
      },
    ],
    { animateVertical: true },
  );

  function getInitialStatus(
    config: ConfigDetailView_ConfigFragment,
    showPublishedView: boolean | null,
  ) {
    if (showPublishedView) return ConfigurationStatus.Published;

    switch (config.state) {
      case ConfigurationStatus.Readytopublish:
        return ConfigurationStatus.Readytopublish;
      case ConfigurationStatus.Draft:
      case ConfigurationStatus.Unpublished:
        return ConfigurationStatus.Draft;
      default:
        return config.draft ? ConfigurationStatus.Draft : ConfigurationStatus.Published;
    }
  }

  const initialStatus = getInitialStatus(configuration, showViewInPublishedState);

  const [configStatus, setConfigStatus] = useState<ConfigurationStatusType>(initialStatus);

  const handleConfigurationStateChange = (status: ConfigurationStatusType) => {
    if (
      status === ConfigurationStatus.Draft &&
      (configuration.state === ConfigurationStatus.Draft ||
        configuration.state === ConfigurationStatus.Readytopublish)
    ) {
      return;
    }
    if (status === ConfigurationStatus.Draft) {
      if (configuration.draft && configuration.draft.state === ConfigurationStatus.Readytopublish) {
        status = ConfigurationStatus.Readytopublish;
      }
    }

    setConfigStatus(status);
    onConfigurationStateChange(status);
  };
  // useEffect(() => {
  //   setTargetRect(headerRef?.current?.getBoundingClientRect());
  // }, [headerRef]);

  const headerHeight = useContentBoxHeight(headerRef);
  const scrollOffset = NAVBAR_HEIGHT + headerHeight + PAGE_CONTENT_PADDING_TOP * 4;
  const activeTab = tabs.find((tab) => tab.isActive);
  const tabsForButtons = ['config-summary', 'settings', 'firmware'];
  const shouldShowPublish =
    tabsForButtons.includes(activeTab?.path || '') &&
    (configuration.state === ConfigurationStatus.Draft ||
      configuration.state === ConfigurationStatus.Unpublished);
  const shouldShowSyncAll =
    configuration.state === ConfigurationStatus.Published && activeTab?.path === 'config-devices';
  const shouldShowDeleteDraft =
    tabsForButtons.includes(activeTab?.path || '') &&
    (configuration.state === ConfigurationStatus.Draft ||
      configuration.state === ConfigurationStatus.Readytopublish);
  const shouldShowMenu =
    tabsForButtons.includes(activeTab?.path || '') &&
    configuration.state === ConfigurationStatus.Published;
  const shouldShowEditButton =
    tabsForButtons.includes(activeTab?.path || '') &&
    (configuration.state === ConfigurationStatus.Published ||
      configuration.state === ConfigurationStatus.Readytopublish) &&
    !configuration.draft;

  useEffect(() => {
    if (
      (configuration.state === ConfigurationStatus.Draft ||
        configuration.state === ConfigurationStatus.Unpublished) &&
      activeTab?.path === 'config-devices'
    ) {
      navigate('config-summary', { replace: true });
    }
  }, [configuration.state, activeTab, navigate]);

  const [createDraft, { loading: isDraftCreateLoading }] = useCreateDraftMutation();

  const handleEdit = async () => {
    try {
      await createDraft({
        variables: {
          configurationId: configuration.id,
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
      onConfigurationStateChange(ConfigurationStatus.Draft);
      setConfigStatus(ConfigurationStatus.Draft);
      editConfigurationModal.onClose();
      navigate('.', {
        state: {
          editConfig: true,
          showPublished: false,
        },
        replace: true,
      });
      toast({
        status: 'info',
        title: t('editdraftMessageTitle'),
        description: t('editdraftMessageDesc'),
      });
    } catch (err) {
      toast({
        status: 'error',
        title: t('errorCreatingDraft'),
        description: t('errorCreatingDraftDesc'),
      });
    }
  };

  const { configurationId } = useParams();
  const handleNavigateToSummary = () => {
    navigate(`../configurations/${configurationId}/config-summary`, {
      state: {
        showPublished: true,
      },
    });
  };
  const toast = useToast();

  const [SyncAllDisplaysConfigurationMutation] = useSyncAllDisplaysToConfigurationMutation();
  const handleSyncAllDisplaysToConfiguration = useCallback(async () => {
    try {
      if (configurationId) {
        await SyncAllDisplaysConfigurationMutation({
          variables: {
            input: {
              id: configurationId,
            },
          },
          refetchQueries: ['ConfigurationDisplaysPage'],
          awaitRefetchQueries: true,
        });
      }
    } catch (err) {
      toast({
        status: 'error',
        title: t('syncConfigurationError'),
        description: fromError(err, 'SyncAllDisplaysConfiguration'),
      });
    }
  }, [configurationId, SyncAllDisplaysConfigurationMutation, toast, t]);

  const [deleteConfiguration] = useDeleteConfigurationMutation();
  const handleDeleteDraft = useCallback(async () => {
    try {
      if (configuration.id) {
        await deleteConfiguration({
          variables: {
            input: {
              id: configuration.id,
            },
          },
          refetchQueries: ['CustomerConfigDetailPage'],
          awaitRefetchQueries: true,
        });

        toast({
          status: 'success',
          title: t('configurationDeleted'),
          description: t('configurationDeletedDesc'),
        });
        setConfigStatus(ConfigurationStatus.Published);
        onConfigurationStateChange(ConfigurationStatus.Published);
      }
    } catch (err) {
      toast({
        status: 'error',
        title: t('configurationDeleteErr'),
        description: t('configurationDeleteErrDesc'),
      });
    }
  }, [configuration.id, deleteConfiguration, toast, t, onConfigurationStateChange]);
  const [PublishConfigurationMutation] = usePublishConfigurationMutation();

  const handlePublish = async () => {
    try {
      if (
        configuration.state == ConfigurationStatus.Draft &&
        configuration?.updatedAt == configuration?.createdAt
      ) {
        toast({
          status: 'warning',
          title: t('publishUnchangedConfigTitle'),
          description: t('publishUnchangedConfigDesc'),
        });
        return;
      }
      if (configuration.id) {
        await PublishConfigurationMutation({
          variables: {
            configurationId: configuration.id,
          },
          refetchQueries: ['CustomerConfigDetailPage'],
          awaitRefetchQueries: true,
        });
        setConfigStatus(ConfigurationStatus.Published);
        onConfigurationStateChange(ConfigurationStatus.Published);
        handleNavigateToSummary();
      }
    } catch (err) {
      toast({
        status: 'error',
        title: t('publishConfigurationError'),
      });
    }
  };
  function Dot() {
    return <Box w="5px" h="5px" bg="gray.600" borderRadius="full" />;
  }
  const deleteConfigurationModal = useDisclosure();

  const editConfigurationModal = useDisclosure();

  return (
    <>
      <Box
        position="sticky"
        top={NAVBAR_HEIGHT}
        marginTop={-1 * PAGE_CONTENT_PADDING_TOP}
        paddingTop={PAGE_CONTENT_PADDING_TOP}
        boxShadow="0 -16px 0 16px #FAFCFF"
        left="0"
        bgColor="blue.25"
        zIndex="sticky"
        ref={headerRef}
      >
        <ConfigDetailHeader
          config={configuration}
          title={title}
          initialState={configuration.state || undefined}
        />
        {isConfigurationStatusToggleEnabled && (
          <ConfigurationStateToggle
            onStateChange={handleConfigurationStateChange}
            value={configStatus}
          />
        )}
        <Flex justify="space-between" direction="row" px={4}>
          <HorizontalTabList tabs={tabs}>
            {tabs.map((tab) => {
              if (shouldShowPublish && tab.path === 'config-devices') {
                return;
              }
              return <HorizontalTab key={tab.path} ref={tab.ref} tab={tab} />;
            })}
            <TabsUnderline tabs={tabs} />
          </HorizontalTabList>
          <HStack spacing={4}>
            {shouldShowSyncAll && (
              <Button
                variant="solid"
                bg="inherent"
                color="gray.600"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ bg: 'gray.100' }}
                onClick={handleSyncAllDisplaysToConfiguration}
                isDisabled={!canUpdate}
              >
                {t('syncAllDisplays')}
              </Button>
            )}
            {shouldShowDeleteDraft && (
              <Button
                variant="solid"
                colorScheme="red"
                onClick={handleDeleteDraft}
                isDisabled={!canDelete}
              >
                {t('deleteDraft')}
              </Button>
            )}
            {shouldShowEditButton && (
              <IconButton
                aria-label={t('edit')}
                icon={<EditIcon />}
                variant="outline"
                borderRadius="md"
                size="lg"
                p={0}
                w="48px"
                h="48px"
                borderColor="gray.300"
                bg="white"
                _hover={{ bg: 'gray.50' }}
                color="gray.600"
                onClick={() => editConfigurationModal.onOpen()}
                isDisabled={isDraftCreateLoading || !canUpdate}
              />
            )}
            {shouldShowPublish && (
              <Button
                variant="solid"
                colorScheme="blue"
                onClick={handlePublish}
                isDisabled={!canUpdate}
              >
                {t('publish')}
              </Button>
            )}
            {shouldShowMenu && (
              <Menu placement="bottom-end">
                <MenuButton
                  as={Button}
                  aria-label="More options"
                  variant="outline"
                  borderRadius="md"
                  size="lg"
                  p={0}
                  w="48px"
                  h="48px"
                  borderColor="gray.300"
                  bg="white"
                  _hover={{ bg: 'gray.50' }}
                >
                  <HStack spacing="4px" justify="center">
                    <Dot />
                    <Dot />
                    <Dot />
                  </HStack>
                </MenuButton>
                <MenuList>
                  <MenuItem
                    p={4}
                    fontWeight={'semibold'}
                    textColor={'grey.400'}
                    onClick={() => {
                      deleteConfigurationModal.onOpen();
                    }}
                    isDisabled={!canDelete}
                  >
                    {t('DeleteConfiguration')}
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
          </HStack>
        </Flex>
      </Box>
      <HorizontalTabPanel>
        {/* Render the routes that are defined in `src/AppRoutes.tsx` */}
        <Outlet
          context={{
            customer,
            configuration,
            refetchData,
            scrollOffset,
            configStatus,
            onConfigurationStateChange: handleConfigurationStateChange,
          }}
        />
      </HorizontalTabPanel>
      <EditConfigurationModal
        editHandler={handleEdit}
        isOpen={editConfigurationModal.isOpen}
        onCancel={editConfigurationModal.onClose}
      />
      <DeleteConfigurationModal
        configurationId={configuration.id}
        isConfigurationSetAsDefault={isDefaultConfiguration}
        isOpen={deleteConfigurationModal.isOpen}
        onCancel={deleteConfigurationModal.onClose}
      />
    </>
  );
}

ConfigDetailView.graphql = {
  fragments: {
    ConfigDetailView_customer: gql`
      fragment ConfigDetailView_customer on Customer {
        id
        ...ConfigDetailSettings_customer
      }
    `,
    ConfigDetailView_config: gql`
      fragment ConfigDetailView_config on ConfigurationModel {
        id
        ...ConfigDetailHeader_config
        ...ConfigDetailSettings_config
        draft {
          id
          ...ConfigDetailHeader_config
          ...ConfigFirmwareVersionSelect_config
          ...ConfigDetailSettings_config
        }
      }
    `,
  },
  mutations: {
    publishConfiguration: gql`
      mutation publishConfiguration($configurationId: String!) {
        publishConfiguration(configurationId: $configurationId) {
          configuration {
            id
            name
            state
          }
        }
      }
    `,
    syncAllDisplaysToConfiguration: gql`
      mutation syncAllDisplaysToConfiguration($input: DisplayBulkSyncConfigurationsInput!) {
        syncAllDisplaysToConfiguration(input: $input)
      }
    `,
    DeleteConfiguraiton: gql`
      mutation DeleteConfiguration($input: DeleteConfigurationsInput!) {
        deleteConfiguration(input: $input)
      }
    `,
  },
};
